import { baseApi } from "../baseApi";

export const managementApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [
      "InsurancePolicies",
      "Payers",
      "PriceListTypes",
      "PriceListSubTypes",
      "PriceLists",
      "Staff",
      "Shifts",
      "Roles",
      "Modules",
      "Permissions",
      "AppointmentTypes",
      "Departments",
      "Specialities",
      "PaymentCardTypes",
      "EpisodeLimitHistory",
      "NahdiPrescriptions",
      "IcdBulkReplaceLogs",
      "IcdCodes",
      "Sfda",
      "ItemFieldReplace",
      "CounterHandoverSummary",
      "Settings",
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getInsurancePolicies: builder.query({
        query: (payload) => ({
          url: "/v3/insurancePolicy/list",
          method: "POST",
          body: payload,
        }),
        providesTags: ["InsurancePolicies"],
      }),

      getPayersList: builder.query({
        query: (payload) => ({
          url: "/v3/payer/list",
          method: "POST",
          body: payload,
        }),
        providesTags: ["Payers"],
      }),

      getPriceListTypes: builder.query({
        query: (payload) => ({
          url: "/v3/pricelist_types/list",
          method: "POST",
          body: payload,
        }),
        providesTags: ["PriceListTypes"],
      }),

      getPriceListSubTypes: builder.query({
        query: (payload) => ({
          url: "/v3/pricelist_subtypes/list",
          method: "POST",
          body: payload,
        }),
        providesTags: ["PriceListSubTypes"],
      }),

      getPriceLists: builder.query({
        query: (payload) => ({
          url: "/v3/pricelists/list",
          method: "POST",
          body: payload,
        }),
        providesTags: ["PriceLists"],
      }),

      getStaffList: builder.query({
        query: (payload) => ({
          url: "/v3/staff/getList",
          method: "POST",
          body: payload,
        }),
        providesTags: ["Staff"],
      }),

      getShiftsList: builder.query({
        query: (payload) => ({
          url: "/v3/shift/getList",
          method: "POST",
          body: payload,
        }),
        providesTags: ["Shifts"],
      }),

      getRolesList: builder.query({
        query: (payload) => ({
          url: "/v3/role/getList",
          method: "POST",
          body: payload,
        }),
        providesTags: ["Roles"],
      }),

      getModulesList: builder.query({
        query: (payload) => ({
          url: "/v3/module/getList",
          method: "POST",
          body: payload,
        }),
        providesTags: ["Modules"],
      }),

      getPermissionsList: builder.query({
        query: (payload) => ({
          url: "/v3/permission/getList",
          method: "POST",
          body: payload,
        }),
        providesTags: ["Permissions"],
      }),

      getAppointmentTypesList: builder.query({
        query: (payload) => ({
          url: "/v3/appointmentTypes/list",
          method: "POST",
          body: payload,
        }),
        providesTags: ["AppointmentTypes"],
      }),

      getDepartmentsList: builder.query({
        query: (payload) => ({
          url: "/v3/department/getList",
          method: "POST",
          body: payload,
        }),
        providesTags: ["Departments"],
      }),

      getSpecialitiesList: builder.query({
        query: (payload) => ({
          url: "/v3/speciality/getList",
          method: "POST",
          body: payload,
        }),
        providesTags: ["Specialities"],
      }),

      getPaymentCardTypes: builder.query({
        query: (payload) => ({
          url: "/v3/card_types/seed_or_list",
          method: "POST",
          body: payload,
        }),
        providesTags: ["PaymentCardTypes"],
      }),

      getEpisodeLimitAuditHistory: builder.query({
        query: (payload) => ({
          url: "/v3/visit/episodeLimitAuditHistory",
          method: "POST",
          body: payload,
        }),
        providesTags: ["EpisodeLimitHistory"],
      }),

      getNahdiPrescriptions: builder.query({
        query: ({ status = "success", ...payload }) => ({
          url: `/v3/pharmacy/nahdi/prescriptions/${status}/list`,
          method: "POST",
          body: payload,
        }),
        providesTags: ["NahdiPrescriptions"],
      }),

      getIcdBulkReplaceLogs: builder.query({
        query: (payload) => ({
          url: "/v3/reports/icd-inactive-report/bulk-replace/logs",
          method: "POST",
          body: payload,
        }),
        providesTags: ["IcdBulkReplaceLogs"],
      }),

      getIcdList: builder.query({
        query: (payload) => ({
          url: "/v3/icd/list",
          method: "POST",
          body: payload,
        }),
        providesTags: ["IcdCodes"],
      }),

      getSfdaList: builder.query({
        query: (payload) => ({
          url: "/v3/sfda/list",
          method: "POST",
          body: payload,
        }),
        providesTags: ["Sfda"],
      }),

      getItemFieldReplaceRuns: builder.query({
        query: () => ({
          url: "/v3/migration/item-field-replace/runs",
          method: "GET",
        }),
        providesTags: ["ItemFieldReplace"],
      }),

      getItemFieldReplaceLogs: builder.query({
        query: ({ runId, page = 1, per_page = 20 }) => ({
          url: `/v3/migration/item-field-replace/${runId}/logs`,
          method: "GET",
          params: { page, per_page },
        }),
        providesTags: ["ItemFieldReplace"],
      }),

      getCounterList: builder.query({
        query: (payload) => ({
          url: "/v3/counter/getList",
          method: "POST",
          body: payload,
        }),
        providesTags: ["CounterHandoverSummary"],
      }),

      getCounterPastReport: builder.query({
        query: (payload) => ({
          url: "/v3/counter/getCounterPastReport",
          method: "POST",
          body: payload,
        }),
      }),

      // --- Insurance Policies Mutations ---
      createInsurancePolicy: builder.mutation({
        query: (body) => ({
          url: "/v3/insurancePolicy/create",
          method: "POST",
          body,
        }),
        invalidatesTags: ["InsurancePolicies"],
      }),
      updateInsurancePolicy: builder.mutation({
        query: (body) => ({
          url: "/v3/insurancePolicy/update",
          method: "PUT",
          body,
        }),
        invalidatesTags: ["InsurancePolicies"],
      }),
      deleteInsurancePolicy: builder.mutation({
        query: (body) => ({
          url: "/v3/insurancePolicy/delete",
          method: "POST",
          body,
        }),
        invalidatesTags: ["InsurancePolicies"],
      }),

      // --- Payers Mutations ---
      createPayer: builder.mutation({
        query: (body) => ({
          url: "/v3/payer/create",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Payers"],
      }),
      updatePayer: builder.mutation({
        query: (body) => ({
          url: "/v3/payer/update",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Payers"],
      }),
      deletePayer: builder.mutation({
        query: (body) => ({
          url: "/v3/payer/delete",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Payers"],
      }),
      getPayerPolicies: builder.query({
        query: (payload) => ({
          url: "/v3/insurancePolicy/getByPayerId",
          method: "POST",
          body: payload,
        }),
        providesTags: ["InsurancePolicies"],
      }),

      // --- Price List Types & Subtypes Mutations ---
      createPriceListType: builder.mutation({
        query: (body) => ({
          url: "/v3/pricelist_types/create",
          method: "POST",
          body,
        }),
        invalidatesTags: ["PriceListTypes"],
      }),
      updatePriceListType: builder.mutation({
        query: (body) => ({
          url: "/v3/pricelist_types/update",
          method: "POST",
          body,
        }),
        invalidatesTags: ["PriceListTypes"],
      }),
      deletePriceListType: builder.mutation({
        query: (body) => ({
          url: "/v3/pricelist_types/delete",
          method: "POST",
          body,
        }),
        invalidatesTags: ["PriceListTypes"],
      }),
      seedPriceListTypes: builder.mutation({
        query: (body = {}) => ({
          url: "/v3/pricelist_types/seed",
          method: "POST",
          body,
        }),
        invalidatesTags: ["PriceListTypes"],
      }),
      createPriceListSubType: builder.mutation({
        query: (body) => ({
          url: "/v3/pricelist_subtypes/create",
          method: "POST",
          body,
        }),
        invalidatesTags: ["PriceListSubTypes"],
      }),
      updatePriceListSubType: builder.mutation({
        query: (body) => ({
          url: "/v3/pricelist_subtypes/update",
          method: "POST",
          body,
        }),
        invalidatesTags: ["PriceListSubTypes"],
      }),
      deletePriceListSubType: builder.mutation({
        query: (body) => ({
          url: "/v3/pricelist_types/delete",
          method: "POST",
          body,
        }),
        invalidatesTags: ["PriceListSubTypes"],
      }),
      seedPriceListSubTypes: builder.mutation({
        query: (body = {}) => ({
          url: "/v3/pricelist_subtypes/seed",
          method: "POST",
          body,
        }),
        invalidatesTags: ["PriceListSubTypes"],
      }),

      // --- Price Lists Mutations ---
      createPriceListItem: builder.mutation({
        query: (body) => ({
          url: "/v3/pricelists/create",
          method: "POST",
          body,
        }),
        invalidatesTags: ["PriceLists"],
      }),
      updatePriceListItem: builder.mutation({
        query: (body) => ({
          url: "/v3/pricelists/update",
          method: "POST",
          body,
        }),
        invalidatesTags: ["PriceLists"],
      }),
      deletePriceListItem: builder.mutation({
        query: (body) => ({
          url: "/v3/pricelists/delete",
          method: "POST",
          body,
        }),
        invalidatesTags: ["PriceLists"],
      }),

      // --- Staff Mutations ---
      createStaff: builder.mutation({
        query: (body) => ({
          url: "/v3/staff/create",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Staff"],
      }),
      updateStaff: builder.mutation({
        query: (body) => ({
          url: "/v3/staff/update",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Staff"],
      }),
      deleteStaff: builder.mutation({
        query: (body) => ({
          url: "/v3/staff/delete",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Staff"],
      }),
      updateStaffStatus: builder.mutation({
        query: (body) => ({
          url: "/v3/staff/status",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Staff"],
      }),

      // --- Shift Mutations ---
      createShift: builder.mutation({
        query: (body) => ({
          url: "/v3/shift/create",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Shifts"],
      }),
      updateShift: builder.mutation({
        query: (body) => ({
          url: "/v3/shift/update",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Shifts"],
      }),
      deleteShift: builder.mutation({
        query: (body) => ({
          url: "/v3/shift/delete",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Shifts"],
      }),

      // --- Module Mutations ---
      createModule: builder.mutation({
        query: (body) => ({
          url: "/v3/module/create",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Modules"],
      }),
      updateModule: builder.mutation({
        query: (body) => ({
          url: "/v3/module/update",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Modules"],
      }),
      deleteModule: builder.mutation({
        query: (body) => ({
          url: "/v3/module/delete",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Modules"],
      }),

      // --- Permission Mutations ---
      createPermission: builder.mutation({
        query: (body) => ({
          url: "/v3/permission/create",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Permissions"],
      }),
      updatePermission: builder.mutation({
        query: (body) => ({
          url: "/v3/permission/update",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Permissions"],
      }),
      deletePermission: builder.mutation({
        query: (body) => ({
          url: "/v3/permission/delete",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Permissions"],
      }),

      // --- Appointment Types Mutations ---
      createAppointmentType: builder.mutation({
        query: (body) => ({
          url: "/v3/appointmentTypes/create",
          method: "POST",
          body,
        }),
        invalidatesTags: ["AppointmentTypes"],
      }),
      updateAppointmentType: builder.mutation({
        query: (body) => ({
          url: "/v3/appointmentTypes/update",
          method: "POST",
          body,
        }),
        invalidatesTags: ["AppointmentTypes"],
      }),
      deleteAppointmentType: builder.mutation({
        query: (body) => ({
          url: "/v3/appointmentTypes/delete",
          method: "POST",
          body,
        }),
        invalidatesTags: ["AppointmentTypes"],
      }),

      // --- Department Mutations ---
      createDepartment: builder.mutation({
        query: (body) => ({
          url: "/v3/department/create",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Departments"],
      }),
      updateDepartment: builder.mutation({
        query: (body) => ({
          url: "/v3/department/update",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Departments"],
      }),
      deleteDepartment: builder.mutation({
        query: (body) => ({
          url: "/v3/department/delete",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Departments"],
      }),
      seedDepartments: builder.mutation({
        query: (body = {}) => ({
          url: "/v3/department/seed",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Departments"],
      }),

      // --- Speciality Mutations ---
      createSpeciality: builder.mutation({
        query: (body) => ({
          url: "/v3/speciality/create",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Specialities"],
      }),
      updateSpeciality: builder.mutation({
        query: (body) => ({
          url: "/v3/speciality/update",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Specialities"],
      }),
      deleteSpeciality: builder.mutation({
        query: (body) => ({
          url: "/v3/speciality/delete",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Specialities"],
      }),
      seedSpecialities: builder.mutation({
        query: (body = {}) => ({
          url: "/v3/speciality/seed",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Specialities"],
      }),

      // --- Payment Card Types Mutations ---
      updatePaymentCardType: builder.mutation({
        query: (body) => ({
          url: "/v3/card_types/update",
          method: "POST",
          body,
        }),
        invalidatesTags: ["PaymentCardTypes"],
      }),
      seedPaymentCardTypes: builder.mutation({
        query: (body = {}) => ({
          url: "/v3/card_types/seed_or_list",
          method: "POST",
          body,
        }),
        invalidatesTags: ["PaymentCardTypes"],
      }),

      // --- ICD Codes Mutations ---
      createIcd: builder.mutation({
        query: (body) => ({
          url: "/v3/icd/create",
          method: "POST",
          body,
        }),
        invalidatesTags: ["IcdCodes"],
      }),
      updateIcd: builder.mutation({
        query: ({ id, ...body }) => ({
          url: `/v3/icd/update/${id}`,
          method: "PUT",
          body,
        }),
        invalidatesTags: ["IcdCodes"],
      }),
      deleteIcd: builder.mutation({
        query: (id) => ({
          url: `/v3/icd/delete/${id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["IcdCodes"],
      }),
      syncIcdFromSupergit: builder.mutation({
        query: (body = {}) => ({
          url: "/v3/icd/sync-from-supergit",
          method: "POST",
          body,
        }),
        invalidatesTags: ["IcdCodes"],
      }),
      syncFavoriteIcd: builder.mutation({
        query: (body = {}) => ({
          url: "/v3/favoriteICD/sync-business",
          method: "POST",
          body,
        }),
        invalidatesTags: ["IcdCodes"],
      }),

      // --- SFDA Mutations ---
      createSfda: builder.mutation({
        query: (body) => ({
          url: "/v3/sfda/create",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Sfda"],
      }),
      updateSfda: builder.mutation({
        query: ({ id, ...body }) => ({
          url: `/v3/sfda/update/${id}`,
          method: "PUT",
          body,
        }),
        invalidatesTags: ["Sfda"],
      }),
      deleteSfda: builder.mutation({
        query: (id) => ({
          url: `/v3/sfda/delete/${id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Sfda"],
      }),
      syncSfdaFromSupergit: builder.mutation({
        query: (body = {}) => ({
          url: "/v3/sfda/sync-from-supergit",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Sfda"],
      }),
      syncFavoriteSfda: builder.mutation({
        query: (body = {}) => ({
          url: "/v3/favoriteItem/sync-sfda-business",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Sfda"],
      }),

      // --- ICD Bulk Replace & Item Field Replace Mutations ---
      startIcdBulkReplace: builder.mutation({
        query: (body) => ({
          url: "/v3/reports/icd-inactive-report/bulk-replace",
          method: "POST",
          body,
        }),
        invalidatesTags: ["IcdBulkReplaceLogs"],
      }),
      startItemFieldReplaceJob: builder.mutation({
        query: (body) => ({
          url: "/v3/migration/item-field-replace/start",
          method: "POST",
          body,
        }),
        invalidatesTags: ["ItemFieldReplace"],
      }),
      revertItemFieldReplaceRun: builder.mutation({
        query: (runId) => ({
          url: `/v3/migration/item-field-replace/${runId}/revert`,
          method: "POST",
        }),
        invalidatesTags: ["ItemFieldReplace"],
      }),

      // --- Settings Query & Mutation ---
      getSettingsList: builder.query({
        query: () => ({
          url: "/v3/settings/list",
          method: "POST",
          body: {},
        }),
        providesTags: ["Settings"],
      }),
      updateSettings: builder.mutation({
        query: (body) => ({
          url: "/v3/settings/update",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Settings"],
      }),
    }),
  });

export const {
  useGetInsurancePoliciesQuery,
  useLazyGetInsurancePoliciesQuery,
  useGetPayersListQuery,
  useLazyGetPayersListQuery,
  useGetPriceListTypesQuery,
  useLazyGetPriceListTypesQuery,
  useGetPriceListSubTypesQuery,
  useLazyGetPriceListSubTypesQuery,
  useGetPriceListsQuery,
  useLazyGetPriceListsQuery,
  useGetStaffListQuery,
  useLazyGetStaffListQuery,
  useGetShiftsListQuery,
  useLazyGetShiftsListQuery,
  useGetRolesListQuery,
  useLazyGetRolesListQuery,
  useGetModulesListQuery,
  useLazyGetModulesListQuery,
  useGetPermissionsListQuery,
  useLazyGetPermissionsListQuery,
  useGetAppointmentTypesListQuery,
  useLazyGetAppointmentTypesListQuery,
  useGetDepartmentsListQuery,
  useLazyGetDepartmentsListQuery,
  useGetSpecialitiesListQuery,
  useLazyGetSpecialitiesListQuery,
  useGetPaymentCardTypesQuery,
  useLazyGetPaymentCardTypesQuery,
  useGetEpisodeLimitAuditHistoryQuery,
  useLazyGetEpisodeLimitAuditHistoryQuery,
  useGetNahdiPrescriptionsQuery,
  useLazyGetNahdiPrescriptionsQuery,
  useGetIcdBulkReplaceLogsQuery,
  useLazyGetIcdBulkReplaceLogsQuery,
  useGetIcdListQuery,
  useLazyGetIcdListQuery,
  useGetSfdaListQuery,
  useLazyGetSfdaListQuery,
  useGetItemFieldReplaceRunsQuery,
  useLazyGetItemFieldReplaceRunsQuery,
  useGetItemFieldReplaceLogsQuery,
  useLazyGetItemFieldReplaceLogsQuery,
  useGetCounterListQuery,
  useLazyGetCounterListQuery,
  useGetCounterPastReportQuery,
  useLazyGetCounterPastReportQuery,

  // Insurance Policies Mutations
  useCreateInsurancePolicyMutation,
  useUpdateInsurancePolicyMutation,
  useDeleteInsurancePolicyMutation,

  // Payers Mutations & Queries
  useCreatePayerMutation,
  useUpdatePayerMutation,
  useDeletePayerMutation,
  useGetPayerPoliciesQuery,
  useLazyGetPayerPoliciesQuery,

  // Price List Types & Subtypes Mutations
  useCreatePriceListTypeMutation,
  useUpdatePriceListTypeMutation,
  useDeletePriceListTypeMutation,
  useSeedPriceListTypesMutation,
  useCreatePriceListSubTypeMutation,
  useUpdatePriceListSubTypeMutation,
  useDeletePriceListSubTypeMutation,
  useSeedPriceListSubTypesMutation,

  // Price Lists Mutations
  useCreatePriceListItemMutation,
  useUpdatePriceListItemMutation,
  useDeletePriceListItemMutation,

  // Staff Mutations
  useCreateStaffMutation,
  useUpdateStaffMutation,
  useDeleteStaffMutation,
  useUpdateStaffStatusMutation,

  // Shift Mutations
  useCreateShiftMutation,
  useUpdateShiftMutation,
  useDeleteShiftMutation,

  // Module Mutations
  useCreateModuleMutation,
  useUpdateModuleMutation,
  useDeleteModuleMutation,

  // Permission Mutations
  useCreatePermissionMutation,
  useUpdatePermissionMutation,
  useDeletePermissionMutation,

  // Appointment Types Mutations
  useCreateAppointmentTypeMutation,
  useUpdateAppointmentTypeMutation,
  useDeleteAppointmentTypeMutation,

  // Department Mutations
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,
  useSeedDepartmentsMutation,

  // Speciality Mutations
  useCreateSpecialityMutation,
  useUpdateSpecialityMutation,
  useDeleteSpecialityMutation,
  useSeedSpecialitiesMutation,

  // Payment Card Types Mutations
  useUpdatePaymentCardTypeMutation,
  useSeedPaymentCardTypesMutation,

  // ICD Codes Mutations
  useCreateIcdMutation,
  useUpdateIcdMutation,
  useDeleteIcdMutation,
  useSyncIcdFromSupergitMutation,
  useSyncFavoriteIcdMutation,

  // SFDA Mutations
  useCreateSfdaMutation,
  useUpdateSfdaMutation,
  useDeleteSfdaMutation,
  useSyncSfdaFromSupergitMutation,
  useSyncFavoriteSfdaMutation,

  // Bulk Replace & Migration Mutations
  useStartIcdBulkReplaceMutation,
  useStartItemFieldReplaceJobMutation,
  useRevertItemFieldReplaceRunMutation,

  // Settings Query & Mutation
  useGetSettingsListQuery,
  useLazyGetSettingsListQuery,
  useUpdateSettingsMutation,
} = managementApi;
